/* /policies/packs — Workspace policy-pack admin.
 *
 * Lists the shipped packs, shows which are active on this workspace,
 * and lets admins toggle packs, disable categories inside a pack,
 * adjust strictness, and add allowlist phrases that should bypass
 * scoring.
 *
 * Keeps the UX intentionally compact — one card per pack, inline
 * controls. No dialogs. Saves on explicit "Save changes" click to
 * avoid partial writes.
 */

import { useEffect, useMemo, useState } from "react";
import { api } from "../api";
import { useAuth } from "../auth-context";

const ACRONYMS = new Set([
  "NHS", "CHI", "HN", "MRN", "PII", "DOB", "UK", "US", "EU", "IBAN", "NI",
  "JWT", "AWS", "GCP", "API", "SSN", "URL", "URI", "IP", "ID",
  "LLP", "LLC", "PLC", "LTD", "INC", "OTP", "2FA", "SSH", "PGP", "RSA", "DSA",
]);
function titleCaseCategory(input: string): string {
  return input
    .replace(/_/g, " ")
    .split(/(\s+|-|\/)/)
    .map((part) => {
      if (!part || /^\s+$/.test(part) || part === "-" || part === "/") return part;
      const up = part.toUpperCase();
      if (ACRONYMS.has(up)) return up;
      return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
    })
    .join("");
}

interface PackInfo {
  id: string;
  name: string;
  version: string;
  description?: string;
  priority: number;
  thresholds: { warn: number; sanitise: number; block: number };
  categories: string[];
  counts: { patterns: number; terms: number; boosters: number };
}

interface Override {
  disabledCategories: string[];
  allowlistPhrases: string[];
  strictness: number;
}

type OverrideMap = Record<string, Override>;

function emptyOverride(): Override {
  return { disabledCategories: [], allowlistPhrases: [], strictness: 1 };
}

export function PacksPage() {
  const { profile, refresh, isDemo } = useAuth();
  const [packs, setPacks] = useState<PackInfo[]>([]);
  const [activeIds, setActiveIds] = useState<string[]>([]);
  const [overrides, setOverrides] = useState<OverrideMap>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [effective, setEffective] = useState<{ packIds: string[]; thresholds: { warn: number; sanitise: number; block: number } } | null>(null);

  // Load packs + current workspace config
  useEffect(() => {
    (async () => {
      try {
        const { packs } = await api.listPolicyPacks();
        setPacks(packs.slice().sort((a, b) => a.priority - b.priority));
        const ws = profile.workspace;
        setActiveIds(ws?.activePackIds && ws.activePackIds.length ? ws.activePackIds : ["general"]);
        const existing = (ws?.packOverrides ?? {}) as OverrideMap;
        setOverrides(Object.fromEntries(
          packs.map((p) => [p.id, { ...emptyOverride(), ...existing[p.id] }])
        ));
      } catch (e: any) {
        setErr(e?.message ?? "Could not load policy packs.");
      } finally {
        setLoading(false);
      }
    })();
  }, [profile.workspace]);

  function togglePack(id: string, on: boolean) {
    if (id === "general") return; // general is always on
    setActiveIds((prev) => on ? Array.from(new Set([...prev, id])) : prev.filter((x) => x !== id));
  }

  function toggleCategory(packId: string, category: string, enabled: boolean) {
    setOverrides((prev) => {
      const cur = prev[packId] ?? emptyOverride();
      const disabled = new Set(cur.disabledCategories);
      if (enabled) disabled.delete(category); else disabled.add(category);
      return { ...prev, [packId]: { ...cur, disabledCategories: Array.from(disabled) } };
    });
  }

  function setStrictness(packId: string, value: number) {
    setOverrides((prev) => {
      const cur = prev[packId] ?? emptyOverride();
      return { ...prev, [packId]: { ...cur, strictness: value } };
    });
  }

  function addAllowlist(packId: string, phrase: string) {
    const p = phrase.trim();
    if (!p) return;
    setOverrides((prev) => {
      const cur = prev[packId] ?? emptyOverride();
      if (cur.allowlistPhrases.includes(p)) return prev;
      return { ...prev, [packId]: { ...cur, allowlistPhrases: [...cur.allowlistPhrases, p].slice(0, 100) } };
    });
  }

  function removeAllowlist(packId: string, phrase: string) {
    setOverrides((prev) => {
      const cur = prev[packId] ?? emptyOverride();
      return { ...prev, [packId]: { ...cur, allowlistPhrases: cur.allowlistPhrases.filter((x) => x !== phrase) } };
    });
  }

  async function save() {
    setErr(null);
    setSaving(true);
    try {
      // Only send overrides for active packs — trims noise in storage.
      const trimmed: OverrideMap = {};
      for (const id of activeIds) {
        const o = overrides[id];
        if (!o) continue;
        // Drop defaults to keep the document tidy.
        if (o.disabledCategories.length || o.allowlistPhrases.length || o.strictness !== 1) {
          trimmed[id] = o;
        }
      }
      const res = await api.updatePackConfig({ activePackIds: activeIds, packOverrides: trimmed });
      setEffective(res.effective);
      await refresh();
    } catch (e: any) {
      setErr(e?.message ?? "Could not save pack configuration.");
    } finally {
      setSaving(false);
    }
  }

  const dirty = useMemo(() => {
    const wsIds: string[] = profile.workspace?.activePackIds ?? ["general"];
    const wsOverrides = (profile.workspace?.packOverrides ?? {}) as OverrideMap;
    if (wsIds.length !== activeIds.length || wsIds.some((id: string, i: number) => id !== activeIds[i])) return true;
    for (const id of activeIds) {
      const a = overrides[id] ?? emptyOverride();
      const b = wsOverrides[id] ?? emptyOverride();
      if (a.strictness !== (b.strictness ?? 1)) return true;
      if ((a.disabledCategories?.length ?? 0) !== (b.disabledCategories?.length ?? 0)) return true;
      if ((a.allowlistPhrases?.length ?? 0) !== (b.allowlistPhrases?.length ?? 0)) return true;
    }
    return false;
  }, [activeIds, overrides, profile.workspace]);

  if (loading) {
    return <div className="sg-page"><div className="sg-muted">Loading policy packs…</div></div>;
  }

  return (
    <div className="sg-page">
      <header className="sg-page-head">
        <div>
          <h1 className="sg-h1">Policy packs</h1>
          <p className="sg-muted" style={{ maxWidth: 640 }}>
            Packs are structured bundles of detection rules for a domain. General is always on.
            Enable more packs as the kind of data you handle expands. Tune a pack's strictness or
            disable categories if a rule fires too often.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {effective && (
            <span className="sg-muted" style={{ fontSize: 12 }}>
              Active: {effective.packIds.join(" + ")} · warn {effective.thresholds.warn} / sanitise {effective.thresholds.sanitise} / block {effective.thresholds.block}
            </span>
          )}
          <button
            className="sg-btn sg-btn-primary"
            onClick={save}
            disabled={!dirty || saving || isDemo}
            title={isDemo ? "Demo workspace is read-only" : ""}
          >
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </header>

      {err && <div className="sg-err" role="alert">{err}</div>}
      {isDemo && (
        <div className="sg-note">Read-only on the demo workspace. Changes won't persist.</div>
      )}

      <div className="sg-packs-grid">
        {packs.map((pack) => {
          const active = activeIds.includes(pack.id);
          const ov = overrides[pack.id] ?? emptyOverride();
          const disabledCats = new Set(ov.disabledCategories);
          return (
            <article key={pack.id} className={`sg-pack-card ${active ? "is-active" : ""}`}>
              <header className="sg-pack-head">
                <div>
                  <h2 className="sg-pack-title">{pack.name}</h2>
                  <p className="sg-pack-desc">{pack.description}</p>
                </div>
                <label className="sg-toggle" aria-label={`Enable ${pack.name}`}>
                  <input
                    type="checkbox"
                    checked={active}
                    disabled={pack.id === "general"}
                    onChange={(e) => togglePack(pack.id, e.target.checked)}
                  />
                  <span className="sg-toggle-slider" />
                </label>
              </header>

              <div className="sg-pack-meta">
                <span>{pack.counts.patterns} patterns</span>
                <span>·</span>
                <span>{pack.counts.terms} terms</span>
                <span>·</span>
                <span>{pack.counts.boosters} boosters</span>
                <span>·</span>
                <span>v{pack.version}</span>
              </div>

              {active && (
                <div className="sg-pack-body">
                  <div className="sg-pack-section">
                    <div className="sg-pack-section-label">Categories</div>
                    <div className="sg-pack-cats">
                      {pack.categories.map((cat) => (
                        <label key={cat} className={`sg-pack-cat ${disabledCats.has(cat) ? "is-off" : ""}`}>
                          <input
                            type="checkbox"
                            checked={!disabledCats.has(cat)}
                            onChange={(e) => toggleCategory(pack.id, cat, e.target.checked)}
                          />
                          <span>{titleCaseCategory(cat)}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="sg-pack-section">
                    <div className="sg-pack-section-label">
                      Strictness
                      <span className="sg-muted sg-pack-section-hint">
                        {ov.strictness.toFixed(2)}× — {ov.strictness < 0.85 ? "stricter" : ov.strictness > 1.15 ? "more lenient" : "default"}
                      </span>
                    </div>
                    <input
                      type="range"
                      min="0.5" max="2" step="0.05"
                      value={ov.strictness}
                      onChange={(e) => setStrictness(pack.id, parseFloat(e.target.value))}
                      className="sg-pack-slider"
                    />
                  </div>

                  <div className="sg-pack-section">
                    <div className="sg-pack-section-label">
                      Allowlist phrases
                      <span className="sg-muted sg-pack-section-hint">Phrases below are ignored by this pack</span>
                    </div>
                    <AllowlistEditor
                      phrases={ov.allowlistPhrases}
                      onAdd={(p) => addAllowlist(pack.id, p)}
                      onRemove={(p) => removeAllowlist(pack.id, p)}
                    />
                  </div>
                </div>
              )}
            </article>
          );
        })}
      </div>
    </div>
  );
}

function AllowlistEditor({ phrases, onAdd, onRemove }: {
  phrases: string[];
  onAdd: (phrase: string) => void;
  onRemove: (phrase: string) => void;
}) {
  const [value, setValue] = useState("");
  return (
    <div className="sg-allowlist">
      <div className="sg-allowlist-chips">
        {phrases.map((p) => (
          <span key={p} className="sg-chip-removable">
            {p}
            <button className="sg-chip-x" onClick={() => onRemove(p)} aria-label={`Remove ${p}`}>×</button>
          </span>
        ))}
        {phrases.length === 0 && <span className="sg-muted" style={{ fontSize: 12 }}>No allowlisted phrases</span>}
      </div>
      <div className="sg-allowlist-add">
        <input
          type="text"
          className="sg-input sg-input-sm"
          placeholder="e.g. Project Atlas"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); onAdd(value); setValue(""); }
          }}
        />
        <button
          className="sg-btn sg-btn-ghost sg-btn-sm"
          onClick={() => { onAdd(value); setValue(""); }}
          disabled={!value.trim()}
        >
          Add
        </button>
      </div>
    </div>
  );
}
